/** 配布QRの受取。未確認の操作IDを再利用して通信再試行の二重加算を防ぐ。 */
import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';
import { invalidateVisitorHomeBootstrapCache } from './visitorHomeBootstrapCache';

import { isPointDistributionManualCode, normalizePointClaimToken } from '../../../shared/utils/pointClaimQr';

/** 配布QRだけを識別する固定prefix。 */
export const POINT_DISTRIBUTION_PREFIX = 'PDQ_';
/** 生値や認証値を含まない未確認操作の保存prefix。 */
const PENDING_PREFIX = 'ikomasai.point-distribution.pending.';
/** 同じタブの同時送信をまとめる。 */
const inFlight = new Map<string, Promise<PointDistributionClaim>>();
/** 公開可能な業務エラー。DBの生messageは表示しない。 */
const ERROR_MESSAGES: Record<string, string> = {
  RATE_LIMIT_EXCEEDED: '入力回数が多すぎます。1分ほど待ってからお試しください。',
  POINT_DISTRIBUTION_INVALID: '配布QRの形式が正しくありません。もう一度読み取ってください。',
  POINT_DISTRIBUTION_NOT_FOUND: 'この配布QRは見つかりません。スタッフに確認してください。',
  POINT_DISTRIBUTION_EXPIRED: 'この配布QRの有効期限が切れています。',
  POINT_DISTRIBUTION_LIMIT: 'この配布QRの1人あたりの受取回数に達しました。',
  POINT_DISTRIBUTION_CONFLICT: '受取要求を確認できません。スタッフに確認してください。',
  POINT_DISTRIBUTION_BALANCE_OVERFLOW: 'ポイントの上限に達しているため受け取れません。',
  POINT_DISTRIBUTION_MAINTENANCE: 'メンテナンス中です。しばらくしてから読み取ってください。',
};
/** 企画の訪問を伴わない確定受取結果。 */
export interface PointDistributionClaim {
  claimed: true;
  visitorId: string;
  distributionQrId: string;
  pointsGranted: number;
  currentPoint: number;
  maxReachedPoint: number;
  pointLogId: string;
  claimCount: number;
  claimsPerVisitor: number;
  eventId: null;
  eventTitle: string;
}

/**
 * scalar JSONの確定結果を検証する。形式エラー時は操作IDを破棄しない。
 * @param raw 未検証のRPC応答
 * @returns スタンプを増やさない受取結果
 */
function parseClaim(raw: unknown): PointDistributionClaim {
  /** DTO候補。 */
  const row = raw as Partial<PointDistributionClaim> | null;
  if (!row || row.claimed !== true || typeof row.visitorId !== 'string'
    || typeof row.distributionQrId !== 'string' || typeof row.pointLogId !== 'string'
    || ![row.pointsGranted, row.currentPoint, row.maxReachedPoint, row.claimCount, row.claimsPerVisitor].every(Number.isSafeInteger)
    || row.pointsGranted < 1 || row.currentPoint < 0 || row.maxReachedPoint < 0
    || row.claimCount < 1 || row.claimCount > row.claimsPerVisitor) {
    throw new AppError('受取結果を確認できませんでした。同じQRでもう一度お試しください。', { code: 'POINT_DISTRIBUTION_FAILED' });
  }
  return { ...row, eventId: null, eventTitle: '配布ポイント' } as PointDistributionClaim;
}

/**
 * 未確認操作IDを保存してから送信する。端末保存が使えない場合は送信しない。
 * @param sessionToken 有効セッション
 * @param token 配布QR生トークン
 * @param storageKey 生値を含まない保存キー
 * @returns 確定した受取結果
 */
async function sendClaim(sessionToken: string, token: string, storageKey: string): Promise<PointDistributionClaim> {
  /** 成功確認まで保持する操作ID。 */
  let requestId: string;
  try {
    requestId = sessionStorage.getItem(storageKey) || crypto.randomUUID();
    sessionStorage.setItem(storageKey, requestId);
  } catch {
    throw new AppError('受取処理を準備できません。ブラウザの保存設定を確認してください。', { code: 'POINT_DISTRIBUTION_STORAGE' });
  }
  /** RPC結果。通信失敗や応答不正では保存済みIDをそのまま残す。 */
  const { data, error } = await getSupabaseClient().rpc('visitor_claim_point_distribution', {
    p_session_token: sessionToken, p_token: token, p_request_id: requestId,
  });
  if (error || data?.errorCode) {
    /** 未検出のJSON応答も固定エラーへ変換する。 */
    const failureMessage = error?.message ?? data.errorCode;
    /** ログと画面へ公開してよい固定codeだけに限定する。 */
    const code = Object.hasOwn(ERROR_MESSAGES, failureMessage) ? failureMessage : 'POINT_DISTRIBUTION_FAILED';
    logError('配布QRの受取に失敗しました。', { code });
    if (['INVALID_SESSION_TOKEN', 'SESSION_NOT_FOUND', 'VISITOR_INACTIVE', 'SESSION_EXPIRED'].includes(failureMessage)) {
      throw new AppError('ログインし直してください。', { code: 'VISITOR_POINTS_CLAIM_SESSION_INVALID' });
    }
    throw new AppError(ERROR_MESSAGES[code] || '受取結果を確認できませんでした。同じQRでもう一度お試しください。', { code: code === 'RATE_LIMIT_EXCEEDED' ? 'POINT_DISTRIBUTION_RATE_LIMITED' : code });
  }
  /** 検証済みの確定結果。 */
  const result = parseClaim(data);
  invalidateVisitorHomeBootstrapCache(sessionToken);
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    throw new AppError('受取結果の保存を確認できません。同じQRでもう一度お試しください。', { code: 'POINT_DISTRIBUTION_STORAGE' });
  }
  return result;
}

/**
 * 配布QRをアカウントごとの上限内で受け取る。
 * @param sessionToken 来場者セッション
 * @param token PDQ形式のQR生値
 * @returns ポイント付与の確定結果
 */
export async function visitorClaimPointDistribution(sessionToken: string, token: string): Promise<PointDistributionClaim> {
  token = normalizePointClaimToken(token);
  if (!sessionToken.trim() || (!/^PDQ_[a-f0-9]{64}$/.test(token) && !isPointDistributionManualCode(token))) {
    throw new AppError(ERROR_MESSAGES.POINT_DISTRIBUTION_INVALID, { code: 'POINT_DISTRIBUTION_INVALID' });
  }
  /** 認証値と生QRを永続化しないための識別用SHA-256。 */
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify([sessionToken.trim(), token])));
  /** 未確認操作だけを保存するキー。 */
  const storageKey = PENDING_PREFIX + Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  /** 既に送信中なら同じPromiseへまとめる。 */
  const existing = inFlight.get(storageKey);
  if (existing) return existing;
  /** 保存→送信→成功確認の1処理。 */
  const promise = sendClaim(sessionToken.trim(), token, storageKey);
  inFlight.set(storageKey, promise);
  try {
    return await promise;
  } finally {
    inFlight.delete(storageKey);
  }
}
